<script setup lang="ts">
import {BlogArticle} from "~/utils/BlogArticle";
import DataView from "primevue/dataview";
import {useRouter} from "vue-router";
import { useUserDataStorage } from "~/store/userdata";

const props = defineProps<{blogArticles:BlogArticle[], title:string, totalRecords:number}>()
const router = useRouter()

const first = defineModel<number>()
const userDataStore = useUserDataStorage()

function plainDescription(value: string | undefined): string {
  if (!value) return ''

  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim()
}
</script>

<template>
  <div class="max-w-5xl mx-auto px-4 pb-16 pt-4">

    <!-- Admin: Hinzufügen -->
    <div
      v-if="userDataStore.loggedIn"
      class="flex items-center justify-center gap-2 mb-7 py-3 border border-dashed border-zinc-600 rounded-xl text-stone-400 text-sm cursor-pointer hover:bg-zinc-800 transition-colors"
      @click="router.push('/blog/article/new')"
    >
      <i class="pi pi-plus"></i> Beitrag hinzufügen
    </div>

    <!-- Leer -->
    <div v-if="!blogArticles.length" class="text-center py-16 text-stone-400">
      Keine Einträge vorhanden.
    </div>

    <DataView v-if="blogArticles.length" :value="blogArticles" paginator :rows="10" :totalRecords="totalRecords" v-model:first="first">
      <template #list="slotProps:{items:BlogArticle[]}">

        <!-- Featured: erstes Element -->
        <div
          v-if="slotProps.items.length > 0"
          class="flex flex-col md:flex-row bg-zinc-900 rounded-2xl overflow-hidden cursor-pointer mb-8 group hover:ring-2 hover:ring-[#E39737] transition-all md:min-h-[320px]"
          @click="router.push('/blog/article/'+slotProps.items[0].slug)"
        >
          <div class="flex-shrink-0 overflow-hidden md:w-[48%]">
            <NuxtImg
              v-if="slotProps.items[0].image.url"
              loading="lazy"
              :src="slotProps.items[0].image.url"
              class="w-full h-[240px] md:h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div v-else class="w-full h-[240px] md:h-full bg-zinc-800" />
          </div>
          <div class="flex flex-col justify-center gap-2 p-7">
            <div class="flex items-center gap-2 flex-wrap">
              <span v-if="slotProps.items[0].publishedAt" class="text-stone-500 text-xs font-medium">
                {{ slotProps.items[0].publishedAt.toLocaleDateString("de") }}
              </span>
              <span v-else class="text-xs px-2 py-0.5 bg-zinc-700 text-stone-300 rounded">Entwurf</span>
              <span v-if="slotProps.items[0].category?.name" class="text-xs px-2 py-0.5 bg-zinc-700 text-[#4C9CD4] rounded font-medium">
                {{ slotProps.items[0].category.name }}
              </span>
            </div>
            <h2 class="text-xl font-bold text-white leading-snug">{{ slotProps.items[0].Title }}</h2>
            <p class="text-stone-400 text-sm leading-relaxed line-clamp-4">{{ plainDescription(slotProps.items[0].Description) }}</p>
            <span class="text-[#E39737] text-sm font-semibold mt-1">Weiterlesen →</span>
          </div>
        </div>

        <!-- Grid: restliche Artikel -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div
            class="flex flex-col bg-zinc-900 rounded-2xl overflow-hidden cursor-pointer group hover:ring-2 hover:ring-[#E39737] transition-all"
            v-for="(item, index) in slotProps.items.slice(1)"
            :key="index"
            @click="router.push('/blog/article/'+item.slug)"
          >
            <div class="overflow-hidden">
              <NuxtImg
                v-if="item.image.url"
                loading="lazy"
                :src="item.image.url"
                class="w-full h-[180px] object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div v-else class="w-full h-[180px] bg-zinc-800" />
            </div>
            <div class="flex flex-col flex-1 p-4">
              <div class="flex items-center gap-2 flex-wrap mb-2">
                <span v-if="item.publishedAt" class="text-stone-500 text-xs font-medium">{{ item.publishedAt.toLocaleDateString("de") }}</span>
                <span v-else class="text-xs px-2 py-0.5 bg-zinc-700 text-stone-300 rounded">Entwurf</span>
                <span v-if="item.category?.name" class="text-xs px-2 py-0.5 bg-zinc-700 text-[#4C9CD4] rounded font-medium">{{ item.category.name }}</span>
              </div>
              <h3 class="text-base font-bold text-white leading-snug mb-2">{{ item.Title }}</h3>
              <p class="text-stone-400 text-sm leading-relaxed line-clamp-3 flex-1 mb-3">{{ plainDescription(item.Description) }}</p>
              <span class="text-[#E39737] text-sm font-semibold">Weiterlesen →</span>
            </div>
          </div>
        </div>

      </template>
    </DataView>
  </div>
</template>

<style>
.p-dataview-content {
  background: none !important;
}
</style>
